import React, { useState } from 'react';
import {
  Alert,
  Button,
  Divider,
  Spinner,
  Tooltip,
} from '@billda/design-system';
import {
  WorkspacePremiumIcon,
  DeleteIcon,
  EditIcon,
} from '@billda/design-system';
import { GangFinancialSummary } from '../molecules/GangFinancialSummary';
import { ListGangsResponse } from '../../api/gangsApi';
import {
  GangCreationStep,
  gangParticipantFromGangMember,
} from '../../types/gangCreationTypes';
import { GangMemberListSection } from '../organisms/GangMemberListSection';
import ConfirmDialog from '../../../../shared/components/ui/ConfirmDialog';

interface GangDetailsPanelProps {
  gang: ListGangsResponse['gangs'][number] | null;
  loading?: boolean;
  onEdit?: (gangId: string, step: GangCreationStep) => void;
  onDelete?: (gangId: string) => void;
  isDeleting?: boolean;
}

// Helper function to format currency (matching the summary step)
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

const GangDetailsPanel: React.FC<GangDetailsPanelProps> = ({
  gang,
  loading = false,
  onEdit,
  onDelete,
  isDeleting = false,
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  if (!gang && !loading) {
    return null;
  }

  const members = gang?.members ?? [];
  const leaders = members.filter(m => m.isLeader);
  const regularMembers = members.filter(m => !m.isLeader);
  const hasAnyMembers = members.length > 0;

  const leaderParticipants = leaders.map(member =>
    gangParticipantFromGangMember(member)
  );
  const memberParticipants = regularMembers.map(member =>
    gangParticipantFromGangMember(member)
  );

  const handleEdit = (step: GangCreationStep) => {
    if (gang && onEdit) {
      onEdit(gang.id, step);
    }
  };

  const renderTitle = () => (
    <div className="mb-4 flex items-center gap-2">
      <h5
        className="type-h5 break-words font-semibold text-content-primary"
        data-testid="gang-name-value"
        data-name={gang?.name ?? ''}
      >
        {gang ? gang.name : 'Gang Overview'}
      </h5>
      {gang && onEdit ? (
        <Tooltip content="Edit name">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleEdit(GangCreationStep.NAME)}
            aria-label="Edit name"
            data-testid="edit-section-name"
            className="size-8"
          >
            <EditIcon aria-hidden="true" className="size-4" />
          </Button>
        </Tooltip>
      ) : null}
    </div>
  );

  return (
    <>
      {loading ? (
        <div
          className="flex min-h-[200px] items-center justify-center px-6 pb-6 pt-1"
          data-testid="gang-details-loading"
        >
          <Spinner size={32} />
        </div>
      ) : (
        gang && (
          <div className="px-6 pb-6 pt-1" data-testid="gang-details-content">
            {renderTitle()}

            {/* Financial Summary */}
            <div data-testid="financial-summary-section">
              <GangFinancialSummary
                leaders={leaderParticipants}
                members={memberParticipants}
                isEqualSplit={gang.isEqualSplit}
                title="Financial Summary"
                showEditButton={Boolean(onEdit)}
                onEditClick={
                  onEdit
                    ? () => handleEdit(GangCreationStep.MEMBER_CONFIG)
                    : undefined
                }
                editButtonTooltip="Edit member configuration"
              />
              <Divider className="my-6" />
            </div>

            {/* Empty members state */}
            {!hasAnyMembers && (
              <Alert
                severity="info"
                className="mb-6"
                data-testid="no-members-message"
              >
                This gang does not have any members yet.
              </Alert>
            )}

            <GangMemberListSection
              members={leaders}
              itemPrefix="leader"
              title={`Leader${leaders.length !== 1 ? 's' : ''}`}
              titleIcon={
                <WorkspacePremiumIcon
                  aria-hidden="true"
                  className="size-5 text-content-warning"
                />
              }
              emptyMessage="No leaders assigned yet."
              avatarClassName="bg-surface-primary text-on-primary"
              formatCurrency={formatCurrency}
              onEdit={onEdit ? handleEdit : undefined}
              editStep={GangCreationStep.LEADERS}
              editTooltip="Edit leaders"
              testIdPrefix="leaders-"
            />

            <GangMemberListSection
              members={regularMembers}
              itemPrefix="member"
              title="Members"
              emptyMessage="No members assigned yet."
              avatarClassName="bg-surface-inverse text-on-inverse"
              formatCurrency={formatCurrency}
              onEdit={onEdit ? handleEdit : undefined}
              editStep={GangCreationStep.MEMBERS}
              editTooltip="Edit members"
              testIdPrefix="members-"
            />

            {onDelete && (
              <>
                <Divider className="my-6" />
                <Button
                  variant="outlineDanger"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  loading={isDeleting}
                  fullWidth
                  data-testid="delete-gang-button"
                >
                  <DeleteIcon aria-hidden="true" className="size-4" />
                  Delete Gang
                </Button>

                <ConfirmDialog
                  open={isDeleteDialogOpen}
                  title="Delete Gang"
                  message={`Are you sure you want to delete "${gang.name}"? This action cannot be undone.`}
                  onCancel={() => setIsDeleteDialogOpen(false)}
                  onConfirm={() => {
                    setIsDeleteDialogOpen(false);
                    onDelete(gang.id);
                  }}
                  confirmText="Delete"
                  cancelText="Cancel"
                />
              </>
            )}
          </div>
        )
      )}
    </>
  );
};

export default GangDetailsPanel;
